import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MobileStepper,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useTheme
} from "@mui/material";
import { Close, KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "../../../app/helpers";
import { RootState } from "../../../app/rootReducer";
import FormConfirm from "../../../components/BookingForm/form-confirm.component";
import FormDateTime from "../../../components/BookingForm/form-date-time.component";
import FormLocation from "../../../components/BookingForm/form-location.component";
import FormWorkplace from "../../../components/BookingForm/form-workplace.component";
import { FormVisitorRegistration } from "../../../components/BookingForm/form-visitor-registration.component";
import {
  clearFloors,
  clearInputs,
  selectBookingLocation,
  setInputs
} from "../../Booking-Form/slices/booking.slice";
import { BookingType } from "../../Booking-Form/typings/booking-inputs";
import { LocationInputs } from "../../Booking-Form/typings/inputs-location";
import {
  isVisitorRegistrationStepComplete,
  visitorRequestsPlaceOrRoom
} from "../../Booking-Form/functions/visitor-registration.functions";
import { useStore } from "react-redux";
import {
  primeVisitorAdminNewBookingDateInputs,
  resetBookingFormForNewVisitorBooking,
  VisitorBookingFormBooker
} from "../functions/reset-booking-form-for-new-visitor-booking.function";

type StepDef = { key: string; label: string };

function buildVisitorAdminDialogSteps(flowHasPlace: boolean, t: (k: string) => string): StepDef[] {
  const steps: StepDef[] = [
    { key: "dt", label: t("Date and Time") },
    { key: "reg", label: t("_visitorBooking_registrationStep") }
  ];
  if (flowHasPlace) {
    steps.push({ key: "loc", label: t("Location") }, { key: "wp", label: t("Workplace") });
  }
  steps.push({ key: "cf", label: t("Confirm") });
  return steps;
}

type NextDisabledArgs = {
  activeStep: number;
  flowHasPlace: boolean;
  inputs: RootState["booking"]["inputs"];
  validDates: boolean;
};

function visitorAdminDialogNextDisabled({
  activeStep,
  flowHasPlace,
  inputs,
  validDates
}: NextDisabledArgs): boolean {
  if (activeStep === 0) {
    return !validDates;
  }
  if (activeStep === 1) {
    return !isVisitorRegistrationStepComplete(inputs);
  }
  if (activeStep === 2 && flowHasPlace) {
    return inputs.selectedLocation == null;
  }
  return true;
}

type P = {
  booker: VisitorBookingFormBooker;
  onBookingCompleted?: () => void;
  onClose: () => void;
  open: boolean;
};

export function VisitorAdminNewBookingDialog({
  booker,
  onBookingCompleted,
  onClose,
  open
}: Readonly<P>) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const store = useStore<RootState>();

  const { settings } = useSelector((state: RootState) => state.login);
  const inputs = useSelector((state: RootState) => state.booking.inputs);

  const [activeStep, setActiveStep] = useState(0);
  const [validDates, setValidDates] = useState(false);
  const [flowHasPlace, setFlowHasPlace] = useState(false);
  const wasOpenRef = useRef(false);

  const confirmStepIndex = flowHasPlace ? 4 : 2;
  const steps = useMemo(() => buildVisitorAdminDialogSteps(flowHasPlace, t), [flowHasPlace, t]);

  const locationToState = useCallback(
    (inp: LocationInputs) => {
      dispatch(
        selectBookingLocation({
          selectedLocation: inp.selectedLocation ?? null,
          locationName: inp.locationName || null,
          isMobileWorking: inp.isMobileWorking || false,
          timezone: inp.timezone,
          automatedSeating: inp.automatedSeating,
          automatedSeatingObjects: inp.automatedSeatingObjects
        })
      );
    },
    [dispatch]
  );

  const locationNextStep = useCallback((to?: number) => {
    if (to === 4) {
      setActiveStep(4);
      return;
    }
    setActiveStep(3);
  }, []);

  const workplaceNextStep = useCallback(() => {
    setActiveStep(4);
  }, []);

  const errorToState = useCallback(
    (error: string) => {
      dispatch(setInputs({ errorConfirm: error }));
    },
    [dispatch]
  );

  const handleConfirmSuccess = useCallback(() => {
    onClose();
    onBookingCompleted?.();
  }, [onBookingCompleted, onClose]);

  const confirmPanel = useMemo(
    () => (
      <FormConfirm
        errorToState={errorToState}
        isResourceBooking
        onResourceBookingClose={handleConfirmSuccess}
      />
    ),
    [errorToState, handleConfirmSuccess]
  );

  useLayoutEffect(() => {
    if (!open) {
      return undefined;
    }
    resetBookingFormForNewVisitorBooking(dispatch, booker);
    primeVisitorAdminNewBookingDateInputs(dispatch, {
      standardEndTime: settings.standardEndTime ?? "17:00",
      standardStartTime: settings.standardStartTime ?? "09:00",
      timezone: settings.timezone
    });
    setActiveStep(0);
    setValidDates(false);
    setFlowHasPlace(false);
    return undefined;
  }, [
    booker.email,
    booker.firstName,
    booker.sub,
    booker.surname,
    dispatch,
    open,
    settings.standardEndTime,
    settings.standardStartTime,
    settings.timezone
  ]);

  useEffect(() => {
    if (open) {
      wasOpenRef.current = true;
      return undefined;
    }
    if (wasOpenRef.current) {
      dispatch(clearInputs());
      dispatch(clearFloors());
      wasOpenRef.current = false;
    }
    return undefined;
  }, [dispatch, open]);

  const handleClose = () => {
    onClose();
  };

  const handleBack = () => {
    if (activeStep === 0) {
      return;
    }
    if (activeStep === 2 && flowHasPlace) {
      dispatch(clearFloors());
      dispatch(
        setInputs({
          selectedLocation: null,
          floorInventoryId: undefined,
          locationName: null
        })
      );
    }
    setActiveStep(prev => prev - 1);
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (!validDates) {
        enqueueSnackbar(t("_visitorBookings_newBooking_invalidDates"), { variant: "warning" });
        return;
      }
      setActiveStep(1);
      return;
    }
    if (activeStep === 1) {
      const stateIn = store.getState().booking.inputs;
      if (!isVisitorRegistrationStepComplete(stateIn)) {
        enqueueSnackbar(t("_visitorBookings_newBooking_incompleteVisitors"), {
          variant: "warning"
        });
        return;
      }
      const needPlace = visitorRequestsPlaceOrRoom(stateIn);
      setFlowHasPlace(needPlace);
      if (!needPlace) {
        dispatch(clearFloors());
        dispatch(
          setInputs({
            selectedLocation: null,
            floorInventoryId: undefined,
            locationName: null
          })
        );
      }
      setActiveStep(2);
    }
  };

  const nextDisabled = visitorAdminDialogNextDisabled({
    activeStep,
    flowHasPlace,
    inputs,
    validDates
  });

  const hideMainNext =
    activeStep === confirmStepIndex || (flowHasPlace && (activeStep === 2 || activeStep === 3));

  const renderBody = () => {
    if (activeStep === 0) {
      return <FormDateTime setValidDates={setValidDates} validDate={validDates} />;
    }
    if (activeStep === 1) {
      return <FormVisitorRegistration />;
    }
    if (activeStep === 2 && !flowHasPlace) {
      return confirmPanel;
    }
    if (activeStep === 2 && flowHasPlace) {
      return <FormLocation locationToState={locationToState} nextStep={locationNextStep} />;
    }
    if (activeStep === 3 && flowHasPlace) {
      return <FormWorkplace nextStep={workplaceNextStep} />;
    }
    if (activeStep === 4 && flowHasPlace) {
      return confirmPanel;
    }
    return null;
  };

  return (
    <Dialog data-testid="visitor-admin-new-booking-dialog" fullWidth maxWidth="lg" open={open}>
      <DialogTitle>
        <Box sx={{ alignItems: "center", display: "flex", justifyContent: "space-between" }}>
          <Typography component="span" variant="h6">
            {t("_visitorBookings_newVisitorBookingTitle")}
          </Typography>
          <IconButton aria-label={t("Close")} onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ overflowX: "hidden" }}>
        <Stepper
          activeStep={activeStep}
          data-testid="visitor-admin-new-booking-stepper"
          sx={{
            backgroundColor: theme.palette.background.default,
            borderRadius: 2,
            display: { xs: "none", md: "flex" },
            mb: 2,
            p: 2
          }}
        >
          {steps.map(step => (
            <Step key={step.key}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ display: { xs: "block", md: "none" }, mb: 2 }}>
          <MobileStepper
            activeStep={activeStep}
            backButton={
              <Button
                color="inherit"
                disabled={activeStep === 0}
                onClick={handleBack}
                size="small"
                sx={{ minHeight: 24, minWidth: 40 }}
              >
                <KeyboardArrowLeft />
              </Button>
            }
            data-testid="visitor-admin-new-booking-mobile-stepper"
            nextButton={
              hideMainNext ? (
                <Box sx={{ minWidth: 80 }} />
              ) : (
                <Button
                  color="primary"
                  data-testid="visitor-admin-new-booking-next"
                  disabled={nextDisabled}
                  onClick={handleNext}
                  size="small"
                  sx={{ minHeight: 24, minWidth: 40 }}
                >
                  <KeyboardArrowRight />
                </Button>
              )
            }
            position="static"
            steps={steps.length}
            sx={{
              backgroundColor: theme.palette.background.default,
              borderRadius: 2,
              pb: 1.75,
              pl: 1,
              pr: 1,
              pt: 1.5
            }}
            variant="dots"
          />
        </Box>

        <Box
          sx={{
            alignItems: "center",
            columnGap: 1,
            display: { xs: "none", md: "grid" },
            gridTemplateColumns: "1fr auto 1fr",
            mb: 2,
            px: 0.5
          }}
        >
          <Box sx={{ justifySelf: "start" }}>
            <Button
              color="secondary"
              data-testid="visitor-admin-new-booking-back"
              disabled={activeStep === 0}
              onClick={handleBack}
              size="small"
              startIcon={<KeyboardArrowLeft />}
            >
              {t("Back")}
            </Button>
          </Box>
          <Box
            aria-hidden
            sx={{ alignItems: "center", display: "flex", gap: 1, justifyContent: "center" }}
          >
            {steps.map((step, index) => (
              <Box
                key={step.key}
                sx={{
                  bgcolor:
                    index === activeStep
                      ? theme.palette.primary.main
                      : theme.palette.action.disabledBackground,
                  borderRadius: "50%",
                  height: 8,
                  opacity: index === activeStep ? 1 : 0.55,
                  width: 8
                }}
              />
            ))}
          </Box>
          <Box sx={{ justifySelf: "end", minHeight: 36 }}>
            {hideMainNext ? null : (
              <Button
                color="primary"
                data-testid="visitor-admin-new-booking-next-desktop"
                disabled={nextDisabled}
                endIcon={<KeyboardArrowRight />}
                onClick={handleNext}
                size="small"
              >
                {t("Next")}
              </Button>
            )}
          </Box>
        </Box>

        <Box sx={{ pb: 2 }}>{inputs.mode === BookingType.VISITOR ? renderBody() : null}</Box>
      </DialogContent>
    </Dialog>
  );
}
